/**
 * 消息去重工具
 * 用于防止在短时间内显示重复的错误/成功消息
 *
 * 使用场景：
 * - 多个并发请求同时失败时，避免显示重复的错误消息
 * - 用户快速重复操作时，避免显示多个相同的提示
 */

import { message } from '@/utils/antdStatic';

/** 去重窗口时间（毫秒） */
const DEDUP_WINDOW = 3000;

/** 消息缓存：记录最近显示的消息及其时间戳 */
const messageCache = new Map<string, number>();

/**
 * 生成消息的唯一键
 * 包含消息类型和内容，确保不同类型的相同内容也能区分
 */
const getMessageKey = (type: string, content: string): string => {
  return `${type}:${content}`;
};

/**
 * 检查消息是否应该被显示
 * @param key 消息的唯一键
 * @returns 是否应该显示
 */
const shouldShowMessage = (key: string): boolean => {
  const now = Date.now();
  const lastShown = messageCache.get(key);

  // 如果在去重窗口内已显示过，跳过
  if (lastShown && now - lastShown < DEDUP_WINDOW) {
    return false;
  }

  // 更新时间戳
  messageCache.set(key, now);

  // 设置定时器清理过期记录
  setTimeout(() => {
    messageCache.delete(key);
  }, DEDUP_WINDOW);

  return true;
};

/**
 * 显示去重后的错误消息
 * @param content 消息内容
 */
export const showDedupError = (content: string): void => {
  const key = getMessageKey('error', content);
  if (shouldShowMessage(key)) {
    message.error(content);
  }
};

/**
 * 显示去重后的成功消息
 * @param content 消息内容
 */
export const showDedupSuccess = (content: string): void => {
  const key = getMessageKey('success', content);
  if (shouldShowMessage(key)) {
    message.success(content);
  }
};

/**
 * 显示去重后的警告消息
 * @param content 消息内容
 */
export const showDedupWarning = (content: string): void => {
  const key = getMessageKey('warning', content);
  if (shouldShowMessage(key)) {
    message.warning(content);
  }
};

/**
 * 显示去重后的信息消息
 * @param content 消息内容
 */
export const showDedupInfo = (content: string): void => {
  const key = getMessageKey('info', content);
  if (shouldShowMessage(key)) {
    message.info(content);
  }
};

/**
 * 清除所有缓存的消息记录
 * 用于测试或需要强制重置的场景
 */
export const clearMessageCache = (): void => {
  messageCache.clear();
};

export default {
  error: showDedupError,
  success: showDedupSuccess,
  warning: showDedupWarning,
  info: showDedupInfo,
  clear: clearMessageCache,
};
